const { get, round, keyBy, isEmpty, invert, startCase } = require('lodash')
const { percentage, downloadCSV } = require('../../common')
const {
  getMasteryLevel,
} = require('../../standardsMasteryReport/standardsPerformanceSummary')
const {
  VIEW_BY_KEYS,
  GROUP_BY_KEYS,
  VIEW_BY_OPTIONS,
  ANALYZE_BY_OPTIONS,
  reportAtOptions,
  ANALYZE_BY_KEYS,
  SCORE_TYPE_KEYS,
  CHART_PAGE_SIZE,
  TABLE_PAGE_SIZE,
  LAMBDA_PAGE_SIZE,
  CSV_FILENAME,
} = require('./constants')

const skillKeyToNameKey = invert(GROUP_BY_KEYS)

const onCsvConvert = (data) => downloadCSV(CSV_FILENAME, data)

const getTableGroupKeys = (reportAt) =>
  reportAtOptions[reportAt]?.tableGroupKeys ||
  reportAtOptions[VIEW_BY_KEYS.DOMAIN].tableGroupKeys

const buildScoreKey = (record, tableGroupKeys) =>
  tableGroupKeys.map((k) => get(record, k) || '-').join('__')

const findExternalBandsGroup = (externalBandGroups = [], test = {}) => {
  const { testCategory, testTitle } = test
  return externalBandGroups.find((bd) => {
    const checkForTestCategory = bd.testCategory === testCategory
    const checkForTestTitle = bd.testTitle ? bd.testTitle === testTitle : true
    return checkForTestCategory && checkForTestTitle
  })
}

const buildTableColumnHeaders = (
  compareBy,
  summaryMetrics = [],
  standardsInfo = [],
  _tableGroupKeys,
  termsById = {}
) => {
  if (isEmpty(summaryMetrics) || !compareBy) return []
  const tableGroupKeys = _tableGroupKeys.slice(0, -1)
  const headers = tableGroupKeys
    .map((groupKey, groupIdx) => {
      const isParentRow = groupIdx === 0
      const isLeafRow = groupIdx === tableGroupKeys.length - 1
      // if middle row, check if there is any data for this group key
      if (!isParentRow && !isLeafRow) {
        const hasHeadersData = summaryMetrics.some(
          (metric) => !!metric[groupKey]
        )
        if (!hasHeadersData) {
          return
        }
      }
      const headerRow = isParentRow ? [startCase(compareBy)] : ['']
      const skillMap = keyBy(standardsInfo, groupKey)
      summaryMetrics.forEach((metric, idx) => {
        if (groupKey === GROUP_BY_KEYS.TEST) {
          const testName = metric.test?.name || '-'
          const termId = metric.test?.termId
          const termName = termsById[termId]?.name || termId || ''
          headerRow.push(`${testName} ${termName}`)
        } else {
          const labelKey = skillKeyToNameKey[groupKey]
          const value = get(skillMap[metric[groupKey]], labelKey) || '-'
          // check if the previous metric has a different value for this group key
          const previousMetric = summaryMetrics[idx - 1] || {}
          const hasDifferentValue =
            previousMetric[groupKey] !== metric[groupKey]
          const headerValue = hasDifferentValue ? value : ''
          headerRow.push(headerValue)
        }
      })
      return headerRow
    })
    .filter(Boolean)

  return headers
}

const getColumnsMetaData = (
  summaryMetrics = [],
  {
    compareBy,
    reportAt = VIEW_BY_KEYS.DOMAIN,
    standardsInfo = [],
    terms = {},
  } = {}
) => {
  const termsById = keyBy(terms, '_id')
  const tableGroupKeys = getTableGroupKeys(reportAt)
  const columnsMap = summaryMetrics.map((metric) => {
    const scoreKey = buildScoreKey(metric, tableGroupKeys)
    return {
      scoreKey,
    }
  })
  const headers = buildTableColumnHeaders(
    compareBy,
    summaryMetrics,
    standardsInfo,
    tableGroupKeys,
    termsById
  )
  return [headers, columnsMap]
}

const getCellScore = (record, analyzeBy, scales, externalBands) => {
  const { test, performance } = record
  const { score, maxScore, masteryScore } = performance
  const rank = Math.round(performance.rank)

  if (analyzeBy === ANALYZE_BY_KEYS.SCORE) {
    const formattedScore = round(score, test.isExternal ? 2 : 0)
    const formattedMaxScore = round(maxScore, test.isExternal ? 2 : 0)
    return maxScore
      ? {
          value: `${percentage(score, maxScore, true)}%`,
          tooltip: `${formattedScore} / ${formattedMaxScore}`,
        }
      : { value: `${Math.round(score)}`, tooltip: `${formattedScore}` }
  }

  if (analyzeBy === ANALYZE_BY_KEYS.MASTERY_LEVEL) {
    if (test.isExternal) {
      const bands = findExternalBandsGroup(externalBands, test)?.bands || []
      const band = bands.find((_band) => _band.rank === rank)
      return {
        value: band?.short_name || band?.name || '-',
        tooltip: band?.name || band?.short_name || null,
      }
    }
    const masteryLevel = getMasteryLevel(masteryScore, scales)
    return {
      value: masteryLevel.masteryLabel || '-',
      tooltip: masteryLevel.masteryName,
    }
  }
  return { value: '-', tooltip: null }
}

const transformTableData = ({
  data = [],
  reportAt = VIEW_BY_OPTIONS[0].key,
  analyzeBy = ANALYZE_BY_OPTIONS[0].key,
  selectedPerformanceBand = { scale: [] },
  externalBands,
}) => {
  const { tableGroupKeys } = reportAtOptions[reportAt]

  const tableData = data.map((record) => {
    const { dimension, values = [] } = record
    const rowScores = values.reduce((result, currentRecord) => {
      const cellScore = getCellScore(
        currentRecord,
        analyzeBy,
        selectedPerformanceBand.scale,
        externalBands
      )
      const scoreKey = tableGroupKeys
        .map((k) => get(currentRecord, k) || '-')
        .join('__')
      return { ...result, [scoreKey]: cellScore }
    }, {})
    return { dimension, ...rowScores }
  })

  return tableData
}

const getCsvRows = (data = [], columnsMap = [], transformCtx = {}) => {
  if (isEmpty(data) || isEmpty(columnsMap)) return []
  const tableData = transformTableData({ data, ...transformCtx })
  const csvRows = tableData.map((metric) => {
    const row = [metric.dimension?.name || '-']
    columnsMap.forEach((column) => {
      const cell = get(metric, column.scoreKey)
      row.push(cell?.value ?? cell ?? '-')
    })
    return row
  })
  return csvRows
}

module.exports = {
  CHART_PAGE_SIZE,
  TABLE_PAGE_SIZE,
  LAMBDA_PAGE_SIZE,
  CSV_FILENAME,
  ANALYZE_BY_KEYS,
  SCORE_TYPE_KEYS,
  onCsvConvert,
  transformTableData,
  getColumnsMetaData,
  buildScoreKey,
  getCsvRows,
  getTableGroupKeys,
  buildTableColumnHeaders,
  findExternalBandsGroup,
}
